import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import useChecklistWizard from '../hooks/useChecklistWizard';
import { StepBar }         from './checklist/StepBar';
import { TemplateSelect }  from './checklist/TemplateSelect';
import { FeatureGrid }     from './checklist/FeatureGrid';
import { CompetitorEntry } from './checklist/CompetitorEntry';
import { RatingPhase }     from './checklist/RatingPhase';
import { Results }         from './checklist/Results';
import HamburgerMenu       from './HamburgerMenu';

/**
 * Thin orchestrator for the DIY Comparison Checklist wizard.
 * All state and behaviour live in useChecklistWizard; this component
 * only wires events to the correct sub-component for each step.
 */
export default function ChecklistWizard() {
  const navigate = useNavigate();
  const w = useChecklistWizard();

  const currentFeature = w.selectedFeatures[w.ratingState.idx];

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100" data-testid="checklist-wizard">
      <HamburgerMenu />
      <div className="border-b border-slate-800 bg-slate-900/90 backdrop-blur-sm px-5 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-base font-bold text-white tracking-tight">DIY Comparison Checklist</h1>
            <p className="text-xs text-slate-500">Powered by TweePorter</p>
          </div>
          <button type="button" data-testid="back-to-tweeporter" onClick={() => navigate('/')}
            className="text-slate-400 hover:text-white text-sm transition-colors flex items-center gap-1.5">
            <ArrowLeft size={14} /> TweePorter
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {w.step > 1 && <StepBar current={w.step} />}

        {w.step === 1 && (
          <TemplateSelect templates={w.templates} loading={w.loading} error={w.error} onSelect={w.selectTemplate} />
        )}

        {w.step === 2 && w.template && (
          <FeatureGrid template={w.template} featureState={w.featureState}
            onToggle={w.toggleFeature} onSetWeight={w.setWeight} onToggleExpand={w.toggleExpand}
            canProceed={w.canProceedFromFeatures}
            onProceed={() => w.setStep(3)} onBack={() => w.setStep(1)} />
        )}

        {w.step === 3 && w.template && (
          <CompetitorEntry vendors={w.vendors} onUpdate={w.updateVendor}
            canProceed={w.canProceedFromCompetitors}
            onProceed={w.startRating} onBack={() => w.setStep(2)} />
        )}

        {w.step === 4 && w.template && currentFeature && (
          <RatingPhase
            feature={currentFeature}
            featureCount={w.selectedFeatures.length}
            ratingIdx={w.ratingState.idx}
            weight={w.featureState.weights[currentFeature.id] ?? currentFeature.default_weight}
            vendors={w.vendors}
            featureRatings={w.ratingState.scores[currentFeature.id] || [0, 0, 0]}
            comment={w.ratingState.comments[currentFeature.id] || ''}
            onRate={(vIdx, val) => w.setScore(currentFeature.id, vIdx, val)}
            onSetComment={text => w.setComment(currentFeature.id, text)}
            onNext={() => w.ratingState.idx < w.selectedFeatures.length - 1 ? w.setRatingIdx(w.ratingState.idx + 1) : w.setStep(5)}
            onPrev={() => w.ratingState.idx > 0 ? w.setRatingIdx(w.ratingState.idx - 1) : w.setStep(3)}
            isLast={w.ratingState.idx === w.selectedFeatures.length - 1}
          />
        )}

        {w.step === 5 && w.template && (
          <Results
            template={w.template} selectedFeatures={w.selectedFeatures}
            vendors={w.vendors} featureWeights={w.featureState.weights}
            ratings={w.ratingState.scores} comments={w.ratingState.comments}
            scores={w.scores} maxPossible={w.maxPossible}
            onExport={w.exportCSV} onReset={w.reset}
          />
        )}
      </div>
    </div>
  );
}
